import type { MotionProps } from 'framer-motion';
import { motion } from 'framer-motion';
import isHotkey from 'is-hotkey';
import React, { forwardRef, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchBarActions } from './SearchBarActionsProvider';
import { cssVar } from '@fabric-app/ui/theme/variables';
import { cssTextEllipsis, mediaMobile } from '@fabric-app/ui/constants/styled-utils';
import { chainShortcutHandlers } from '@fabric-app/business/keyboardShortcuts/utils';

const StyledInput = styled(motion.input)`
  display: flex;
  height: 100%;
  padding: 0 8px;
  border: none;
  background: transparent;
  font-size: 15px;
  line-height: 21px;
  flex-shrink: 1;
  flex-grow: 1;
  color: ${cssVar['color-text-primary']};

  &:only-child {
    flex-grow: 1;
  }

  &:focus {
    outline: none;
  }

  &::placeholder {
    color: ${cssVar['color-text-placeholder']};
  }

  ${mediaMobile} {
    padding: 4px;
  }

  ${cssTextEllipsis}
  min-width: 1px;
  width: 1px;
  flex-grow: 1;
`;

const InvisibleSpan = styled.span`
  font-size: 15px;
  line-height: 21px;
  min-width: 230px;
  ${mediaMobile} {
    min-width: 100px;
  }
  padding: 0 8px;

  position: absolute;
  pointer-events: none;
  opacity: 0;

  // make sure to preserve all spaces so the width is calculated correctly
  white-space: pre;
`;

interface SearchInputProps
  extends MotionProps,
    Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof MotionProps> {}
const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  ({ value, onChange, onKeyDown, ...props }, forwardedRef) => {
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    const spanRef = React.useRef<HTMLSpanElement>(null);

    const { focusNextOption, focusPreviousOption } = useSearchBarActions();

    const onKeyDownHandler = (e: React.KeyboardEvent<HTMLInputElement>) => {
      const element = e.currentTarget;
      chainShortcutHandlers(
        e,
        [
          /**
           * Handle the left arrow keydown event and focus the previous filter pill (role="option") or input (role="input")
           */
          (e) => {
            if (!isHotkey('left', e)) return false;

            // if focused on an input and not at the beginning of the input, ignore the left arrow key
            if (element.matches(':focus') && element.selectionStart !== 0) {
              return false;
            }

            return Boolean(focusPreviousOption());
          },
          /**
           * Handle the right arrow keydown event and focus the next filter pill (role="option") or input (role="input")
           */
          (e) => {
            if (!isHotkey('right', e)) return false;

            // if focused on an input and not at the end of the input, ignore the right arrow key
            if (element.matches(':focus') && element.selectionStart !== element.value.length) {
              return false;
            }

            return Boolean(focusNextOption());
          },
        ],
        {
          preventDefaultOnHandle: true,
          stopPropagationOnHandle: true,
        },
      );

      if (onKeyDown) {
        onKeyDown(e);
      }
    };

    useEffect(() => {
      const element = inputRef.current;
      if (!element) return;

      const newWidth = `${spanRef.current?.offsetWidth ?? 0}px`;
      element.style.width = newWidth;
      element.style.minWidth = newWidth;
    }, [value]);

    return (
      <>
        <InvisibleSpan ref={spanRef}>{value}</InvisibleSpan>
        <StyledInput
          ref={(el) => {
            inputRef.current = el;
            if (forwardedRef) {
              if (typeof forwardedRef === 'function') {
                forwardedRef(el);
              } else {
                forwardedRef.current = el;
              }
            }
          }}
          onKeyDown={onKeyDownHandler}
          value={value}
          onChange={onChange}
          {...props}
        />
      </>
    );
  },
);

SearchInput.displayName = 'SearchInput';

export default SearchInput;
